import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import { obtenerCitas, obtenerCitasDoctor } from '../../api/citaService';
import { AuthContext } from '../../context/AuthContext';
import GlobalDataTable from '../common/GlobalDataTable';

const Dashboard = () => {
  const { currentUser } = useContext(AuthContext);
  const [citas, setCitas] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    const cargarCitas = async () => {
      try {
        setIsLoading(true);
        let response;
        
        // Verificar si el usuario es un doctor o un admin
        if (currentUser?.rol === 'DOCTOR') {
          // Si es doctor, obtener solo sus citas
          response = await obtenerCitasDoctor();
          console.log('Dashboard - Citas del doctor cargadas:', response.data);
        } else {
          // Si es admin, obtener todas las citas
          response = await obtenerCitas();
          console.log('Dashboard - Todas las citas cargadas:', response.data);
        }
        
        // Convertir las fechas de string a objetos Date
        if (response.data && response.data.data) {
          const citasData = response.data.data.citas || [];
          console.log('Dashboard - Cantidad de citas encontradas:', citasData.length);
          
          setCitas(citasData.map(cita => ({
            ...cita,
            fecha: new Date(cita.fecha),
            // Asegurar que todos los campos necesarios existan
            servicio: {
              ...cita.servicio,
              titulo: cita.servicio.nombre || cita.servicio.titulo
            },
            estado: cita.estado?.toLowerCase() || 'pendiente',
            pago: cita.pago || { estado: 'pendiente' }
          })));
        } else {
          console.error('Formato de respuesta inesperado:', response.data);
          setCitas([]);
        }
        setError(null);
      } catch (err) {
        console.error('Error al cargar citas:', err);
        setError('No se pudieron cargar las citas. Por favor, intente de nuevo más tarde.');
      } finally {
        setIsLoading(false);
      }
    };
    
    if (currentUser) {
      cargarCitas();
    }
  }, [currentUser]);
  
  // Contadores para las estadísticas
  const totalCitas = citas.length;
  const citasHoy = citas.filter(cita => {
    const today = new Date();
    return cita.fecha.getDate() === today.getDate() && 
           cita.fecha.getMonth() === today.getMonth() && 
           cita.fecha.getFullYear() === today.getFullYear();
  }).length;
  
  const citasPendientes = citas.filter(cita => cita.estado === 'pendiente').length;
  const citasConfirmadas = citas.filter(cita => cita.estado === 'confirmada').length;
  const pagosCompletados = citas.filter(cita => cita.pago.estado === 'completado').length;
  
  // Próximas citas (ordenadas por fecha)
  const proximas = [...citas]
    .filter(cita => ['confirmada', 'pendiente'].includes(cita.estado))
    .sort((a, b) => a.fecha - b.fecha)
    .slice(0, 5); // Mostrar solo las 5 próximas

  const columnasProximasCitas = [
    {
      key: 'paciente',
      header: 'Paciente',
      render: (cita) => `${cita.paciente?.nombre || ''} ${cita.paciente?.apellido || ''}`,
    },
    {
      key: 'servicio',
      header: 'Servicio',
      render: (cita) => cita.servicio?.titulo || cita.servicio?.nombre || 'Servicio sin nombre',
    },
    {
      key: 'fecha',
      header: 'Fecha',
      render: (cita) => (
        cita.fecha.toLocaleString('es-ES', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        })
      ),
    },
    {
      key: 'estado',
      header: 'Estado',
      render: (cita) => (
        <span
          className="global-badge"
          style={{
            backgroundColor: cita.estado === 'confirmada' ? '#e8f5e9' : '#fff3e0',
            color: cita.estado === 'confirmada' ? '#4caf50' : '#ff9800'
          }}
        >
          {cita.estado === 'confirmada' ? 'Confirmada' : 'Pendiente'}
        </span>
      ),
    },
    {
      key: 'acciones',
      header: 'Acciones',
      render: (cita) => (
        <Link to={`/dashboard/citas/${cita.id}`} className="global-table-btn global-table-btn--info">
          Ver detalles
        </Link>
      ),
    },
  ];
    
  // Agregar logs para depuración
  console.log('Dashboard - Total de citas:', citas.length);
  console.log('Dashboard - Próximas citas:', proximas.length);
  
  return (
    <div style={{ padding: '20px' }}>
      <h2 style={{ marginBottom: '20px' }}>Dashboard</h2>
      
      {/* Tarjetas de estadísticas */}
      <div style={{ display: 'flex', gap: '20px', marginBottom: '30px', flexWrap: 'wrap' }}>
        <div style={{ 
          flex: 1, 
          backgroundColor: 'white', 
          padding: '20px', 
          borderRadius: '10px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          minWidth: '200px'
        }}>
          <div style={{ fontSize: '14px', color: '#666' }}>Total Citas</div>
          <div style={{ fontSize: '32px', fontWeight: 'bold', marginTop: '10px' }}>
            {isLoading ? '...' : totalCitas}
          </div>
        </div>
        
        <div style={{ 
          flex: 1, 
          backgroundColor: 'white', 
          padding: '20px', 
          borderRadius: '10px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          minWidth: '200px'
        }}>
          <div style={{ fontSize: '14px', color: '#666' }}>Citas Hoy</div>
          <div style={{ fontSize: '32px', fontWeight: 'bold', marginTop: '10px' }}>
            {isLoading ? '...' : citasHoy}
          </div>
        </div>
        
        <div style={{ 
          flex: 1, 
          backgroundColor: 'white', 
          padding: '20px', 
          borderRadius: '10px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          minWidth: '200px'
        }}>
          <div style={{ fontSize: '14px', color: '#666' }}>Citas Pendientes</div>
          <div style={{ fontSize: '32px', fontWeight: 'bold', marginTop: '10px' }}>
            {isLoading ? '...' : citasPendientes}
          </div>
        </div>
        
        <div style={{ 
          flex: 1, 
          backgroundColor: 'white', 
          padding: '20px', 
          borderRadius: '10px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          minWidth: '200px'
        }}>
          <div style={{ fontSize: '14px', color: '#666' }}>Pagos Completados</div>
          <div style={{ fontSize: '32px', fontWeight: 'bold', marginTop: '10px' }}>
            {isLoading ? '...' : pagosCompletados}
          </div>
        </div>
      </div>
      
      {/* Próximas citas */}
      <div style={{ 
        backgroundColor: 'white', 
        padding: '20px', 
        borderRadius: '10px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        marginBottom: '30px'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h3 style={{ margin: '0' }}>Próximas Citas</h3>
          <Link to="/dashboard/citas-doctor" style={{ textDecoration: 'none', color: '#3498db' }}>
            Ver todas
          </Link>
        </div>
        
        {isLoading && (
          <div style={{ padding: '20px', textAlign: 'center' }}>
            <p>Cargando citas...</p>
          </div>
        )}
        
        {error && (
          <div style={{ padding: '20px', textAlign: 'center', color: '#f44336' }}>
            <p>{error}</p>
          </div>
        )}
        
        {!isLoading && !error && (
          <GlobalDataTable
            columns={columnasProximasCitas}
            rows={proximas}
            rowKey="id"
            emptyMessage="No hay citas próximas"
            emptyIcon={<i className="fas fa-calendar-check"></i>}
          />
        )}
      </div>
      
      {/* Estados de Citas */}
      <div style={{ 
        backgroundColor: 'white', 
        padding: '20px', 
        borderRadius: '10px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      }}>
        <h3 style={{ marginBottom: '20px' }}>Estado de Citas</h3>
        
        {isLoading && (
          <div style={{ padding: '20px', textAlign: 'center' }}>
            <p>Cargando estadísticas...</p>
          </div>
        )}
        
        {error && (
          <div style={{ padding: '20px', textAlign: 'center', color: '#f44336' }}>
            <p>{error}</p>
          </div>
        )}
        
        {!isLoading && !error && (
          <div style={{ display: 'flex', justifyContent: 'space-around', flexWrap: 'wrap', gap: '15px' }}>
            <div style={{ flex: 1, textAlign: 'center', minWidth: '150px' }}>
              <div style={{ 
                width: '100px',
                height: '100px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 15px auto',
                fontSize: '24px',
                fontWeight: 'bold',
                backgroundColor: '#e8f5e9',
                color: '#4caf50'
              }}>
                {citasConfirmadas}
              </div>
              <div>Confirmadas</div>
            </div>
            
            <div style={{ flex: 1, textAlign: 'center', minWidth: '150px' }}>
              <div style={{ 
                width: '100px',
                height: '100px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 15px auto',
                fontSize: '24px',
                fontWeight: 'bold',
                backgroundColor: '#fff3e0',
                color: '#ff9800'
              }}>
                {citasPendientes}
              </div>
              <div>Pendientes</div>
            </div>
            
            <div style={{ flex: 1, textAlign: 'center', minWidth: '150px' }}>
              <div style={{ 
                width: '100px',
                height: '100px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 15px auto',
                fontSize: '24px',
                fontWeight: 'bold',
                backgroundColor: '#e3f2fd',
                color: '#2196f3'
              }}>
                {pagosCompletados}
              </div>
              <div>Pagos Realizados</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;